import { TextInput } from '@frontend/components/basic-forms';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { FaCheck, FaXmark } from 'react-icons/fa6';
import { LuTriangleAlert } from 'react-icons/lu';
import { FormattedMessage, useIntl } from 'react-intl';

import { useWizardModalScreenNavigation } from '../../wizard-modal-navigation.context';
import useEnterTrackingNumber from './enter-tracking-number.controller';
import ExistingTrackingNumberMatch from './existing-tracking-number-match.component';

const ID = 'enter-tracking-number';

const EnterTrackingNumber = (props: { wizard_id: string }) => {
    const intl = useIntl();
    const viewProps = useEnterTrackingNumber(props);

    useWizardModalScreenNavigation({
        onContinue: viewProps.onSubmit,
        continueDisabled: viewProps.submitDisabled
    });

    return (
        <div
            id={ID}
            className='flex w-full flex-col gap-3'>
            <div className='flex items-start justify-start self-stretch'>
                <div className='text-foreground-default justify-start text-base font-medium'>{CommonMessage.FORMS.PROVIDE_THE_FOLLOWING}</div>
            </div>

            <div
                data-state='Open'
                className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                <div className='inline-flex items-center justify-between self-stretch'>
                    <div className='flex items-center justify-start gap-4'>
                        <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                            {viewProps.trackingNumber.trim().length > 0 ? (
                                <FaCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            ) : (
                                <FaXmark className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            )}
                            <ICONS.ENTITY.TRANSACTION className='size-1/2' />
                        </div>
                        <div className='inline-flex flex-col items-start justify-center gap-1'>
                            <div className='text-foreground-default justify-start text-base font-semibold'>
                                {CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER}
                            </div>
                        </div>
                    </div>
                </div>

                <div className='flex w-full items-end gap-2'>
                    <TextInput
                        className='w-full'
                        value={viewProps.trackingNumber}
                        onChange={viewProps.setTrackingNumber}
                    />
                    <button
                        type='button'
                        disabled={viewProps.isGeneratingTrackingNumber}
                        className='hover:bg-brand-light text-brand-primary disabled:text-brand-muted disabled:hover:text-brand-muted hover:text-brand-secondary inline-flex h-11 shrink-0 cursor-pointer items-center justify-center gap-2.5 rounded-md px-4 disabled:cursor-not-allowed disabled:hover:bg-inherit'
                        onClick={() => {
                            void viewProps.onGenerateTrackingNumber();
                        }}>
                        <span className='text-base font-medium'>
                            {intl.formatMessage({
                                id: 'platform.wizard.enter-tracking-number.generate',
                                description: 'Button label that generates a unique tracking number',
                                defaultMessage: 'Generate'
                            })}
                        </span>
                    </button>
                </div>

                <div className='text-brand-muted text-sm'>
                    <FormattedMessage
                        id='platform.wizard.enter-tracking-number.required'
                        description='Validation hint indicating the tracking number is required before continuing'
                        defaultMessage='Tracking number is required.'
                    />
                </div>
            </div>

            {viewProps.existingTransactions && viewProps.existingTransactions.length > 0 && (
                <div className='flex w-full flex-col gap-3'>
                    <div className='bg-status-warning/20 border-status-warning flex items-start gap-3 rounded-xl border p-4'>
                        <LuTriangleAlert className='text-status-warning size-5 shrink-0' />
                        <div className='text-foreground-default text-sm font-medium'>
                            <FormattedMessage
                                id='platform.wizard.enter-tracking-number.already-exists'
                                description='Warning shown when the entered tracking number already belongs to an existing transaction'
                                defaultMessage='This tracking number is already in use. Review the existing transaction below or use a different tracking number before continuing.'
                            />
                        </div>
                    </div>

                    <div className='grid w-full grid-cols-2 gap-2'>
                        {viewProps.existingTransactions.map((transaction) => (
                            <ExistingTrackingNumberMatch
                                key={transaction.id}
                                transaction={transaction}
                                currentUserId={viewProps.currentUserId}
                                isContinuing={viewProps.continuingTransactionId === transaction.id}
                                onContinueWithTransaction={(existingTransaction) => {
                                    void viewProps.onContinueWithExistingTransaction(existingTransaction);
                                }}
                                onTransactionDeleted={() => {
                                    void viewProps.refreshExistingTransactions();
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EnterTrackingNumber;
